import { retrieveRawLaunchParams } from "@tma.js/sdk-react";
import { useEffect, useMemo, useState } from "react";

export interface TelegramUserData {
  id?: number;
  firstName?: string;
  lastName?: string;
  username?: string;
  languageCode?: string;
  isPremium?: boolean;
  photoUrl?: string;
}

export interface TelegramLaunchInfo {
  isTelegram: boolean;
  startParam: string | null;
  platform: string;
  version: string;
  user: TelegramUserData | null;
  telegramId?: number;
  rawLaunchParams: string;
}

/**
 * Determines whether a startParam indicates launching the Second Mini App (Sale Support).
 */
export function isSecondMiniAppParam(startParam?: string | null): boolean {
  if (!startParam) return false;
  const normalized = startParam.trim().toLowerCase();
  return /^(sale[-_]?support|sell[-_]?support|sales?|tasks?|app2|second[-_]?app)/i.test(normalized);
}

/**
 * Helper to parse a JSON user string safely, handling single or double URI encoding.
 */
function parseTelegramUserString(userStr: string): TelegramUserData | null {
  try {
    let parsed: any;
    try {
      parsed = JSON.parse(userStr);
    } catch {
      parsed = JSON.parse(decodeURIComponent(userStr));
    }
    if (parsed && typeof parsed === "object") {
      const idNum = Number(parsed.id);
      return {
        id: Number.isSafeInteger(idNum) && idNum > 0 ? idNum : undefined,
        firstName: parsed.first_name || parsed.firstName,
        lastName: parsed.last_name || parsed.lastName,
        username: parsed.username,
        languageCode: parsed.language_code || parsed.languageCode,
        isPremium: Boolean(parsed.is_premium || parsed.isPremium),
        photoUrl: parsed.photo_url || parsed.photoUrl,
      };
    }
  } catch {
    // Ignore invalid JSON
  }
  return null;
}

/**
 * Pure synchronous extractor for Telegram launch parameters, user data, and startParam.
 * Inspects Telegram WebApp global, location.hash, location.search, and TMA launch storage.
 */
export function getTelegramLaunchData(): TelegramLaunchInfo {
  if (typeof window === "undefined") {
    return {
      isTelegram: false,
      startParam: null,
      platform: "unknown",
      version: "unknown",
      user: null,
      rawLaunchParams: "",
    };
  }

  const tg = window.Telegram?.WebApp;
  const hash = window.location.hash ? window.location.hash.replace(/^#/, "") : "";
  const search = window.location.search ? window.location.search.replace(/^\?/, "") : "";
  const storedRaw = window.sessionStorage?.getItem("tmajs:raw-launch-params") || "";
  let sdkRaw = "";
  try {
    sdkRaw = retrieveRawLaunchParams();
  } catch {}

  const hashParams = new URLSearchParams(hash);
  const searchParams = new URLSearchParams(search);
  const storedParams = new URLSearchParams(storedRaw);
  const sdkParams = new URLSearchParams(sdkRaw);

  // 1. Resolve startParam from all potential locations
  const startParam =
    tg?.initDataUnsafe?.start_param ||
    hashParams.get("tgWebAppStartParam") ||
    hashParams.get("startapp") ||
    hashParams.get("start_param") ||
    searchParams.get("tgWebAppStartParam") ||
    searchParams.get("startapp") ||
    searchParams.get("start_param") ||
    storedParams.get("tgWebAppStartParam") ||
    sdkParams.get("tgWebAppStartParam") ||
    null;

  // 2. Resolve user data
  let user: TelegramUserData | null = null;
  if (tg?.initDataUnsafe?.user) {
    const u = tg.initDataUnsafe.user;
    const idNum = Number(u.id);
    user = {
      id: Number.isSafeInteger(idNum) && idNum > 0 ? idNum : undefined,
      firstName: u.first_name,
      lastName: u.last_name,
      username: u.username,
      languageCode: u.language_code,
      isPremium: Boolean(u.is_premium),
      photoUrl: u.photo_url,
    };
  }

  // Fallback to initData strings
  if (!user?.id) {
    const initDataCandidates = [
      tg?.initData,
      hashParams.get("tgWebAppData"),
      searchParams.get("tgWebAppData"),
      storedParams.get("tgWebAppData"),
      sdkParams.get("tgWebAppData"),
    ].filter(Boolean) as string[];

    for (const initStr of initDataCandidates) {
      const dataParams = new URLSearchParams(initStr);
      const userStr = dataParams.get("user");
      if (userStr) {
        user = parseTelegramUserString(userStr);
        if (user?.id) break;
      }
    }
  }

  // 3. Fallback telegramId from query/hash/startParam (e.g., ?telegram_id=123 or startapp=uid_123)
  let telegramId = user?.id;
  if (!telegramId) {
    const directId =
      searchParams.get("telegram_id") ||
      searchParams.get("chat_id") ||
      searchParams.get("user_id") ||
      hashParams.get("telegram_id") ||
      hashParams.get("chat_id");
    if (directId) {
      const parsedDirect = Number(directId);
      if (Number.isSafeInteger(parsedDirect) && parsedDirect > 0) {
        telegramId = parsedDirect;
      }
    }
  }

  if (!telegramId && startParam) {
    const numericMatch = startParam.match(/(?:uid|user)?_?(\d{5,})/i);
    if (numericMatch) {
      const parsedNum = Number(numericMatch[1]);
      if (Number.isSafeInteger(parsedNum) && parsedNum > 0) {
        telegramId = parsedNum;
      }
    }
  }

  // 4. Platform and Version
  const platform =
    tg?.platform ||
    hashParams.get("tgWebAppPlatform") ||
    searchParams.get("tgWebAppPlatform") ||
    storedParams.get("tgWebAppPlatform") ||
    sdkParams.get("tgWebAppPlatform") ||
    "web";

  const version =
    tg?.version ||
    hashParams.get("tgWebAppVersion") ||
    searchParams.get("tgWebAppVersion") ||
    storedParams.get("tgWebAppVersion") ||
    sdkParams.get("tgWebAppVersion") ||
    "unknown";

  const isTelegram = Boolean(
    tg?.initData ||
    hashParams.get("tgWebAppData") ||
    searchParams.get("tgWebAppData") ||
    sdkParams.get("tgWebAppData") ||
    storedRaw.length > 0 ||
    platform !== "web"
  );

  return {
    isTelegram,
    startParam,
    platform,
    version,
    user,
    telegramId,
    rawLaunchParams: sdkRaw || storedRaw || hash || search,
  };
}

/**
 * Reusable hook to safely access Telegram Mini App launch parameters,
 * start_param, and user context across multiple Mini Apps.
 */
export function useTelegramLaunchInfo(): TelegramLaunchInfo {
  const rawParams = useMemo(() => {
    try {
      return retrieveRawLaunchParams();
    } catch {
      return "";
    }
  }, []);
  const [windowUser, setWindowUser] = useState<TelegramUserData | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Telegram?.WebApp?.initDataUnsafe?.user) {
      const u = window.Telegram.WebApp.initDataUnsafe.user;
      const idNum = Number(u.id);
      setWindowUser({
        id: Number.isSafeInteger(idNum) && idNum > 0 ? idNum : undefined,
        firstName: u.first_name,
        lastName: u.last_name,
        username: u.username,
        languageCode: u.language_code,
        isPremium: Boolean(u.is_premium),
        photoUrl: u.photo_url,
      });
    }
  }, []);

  return useMemo(() => {
    const launchData = getTelegramLaunchData();
    if (windowUser && !launchData.user) {
      launchData.user = windowUser;
      launchData.telegramId = windowUser.id;
    }
    return launchData;
  }, [rawParams, windowUser]);
}
